import React, { useMemo } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { teacherModuleAPI } from '../../services/api';

function formatModulesFromAssessment(assessmentData) {
  const modules = assessmentData?.modules;
  if (!Array.isArray(modules) || !modules.length) return null;
  return modules
    .map((m) => {
      const name = m.moduleName || m.module_name || m.name || m.type || 'Module';
      const level = m.level ? ` (${m.level})` : '';
      const remarks = m.remarks || m.notes || '';
      return remarks ? `${name}${level}: ${remarks}` : `${name}${level}`;
    })
    .join(' · ');
}

export default function TeachingHandover() {
  const { studentId } = useParams();
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batchId') || undefined;
  const navigate = useNavigate();

  const query = useQuery(
    ['teachingHandover', studentId, batchId],
    async () => {
      const res = await teacherModuleAPI.getTeachingHandover(studentId, batchId ? { batchId } : undefined);
      return res.data?.data;
    },
    { enabled: !!studentId, retry: false }
  );

  const data = query.data;
  const title = useMemo(() => {
    if (!data?.student) return 'Teaching Handover';
    return `Teaching Handover — ${data.student.name}`;
  }, [data]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ textTransform: 'none' }}>
          Back
        </Button>
        <Breadcrumbs>
          <Link component={RouterLink} underline="hover" color="inherit" to="/teacher/upcoming-batches">
            Teacher
          </Link>
          <Typography color="text.primary">Teaching handover</Typography>
        </Breadcrumbs>
      </Stack>

      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>

      {query.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {query.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {query.error?.response?.data?.message || query.error?.message || 'Failed to load teaching handover'}
        </Alert>
      ) : null}

      {data ? (
        <Stack spacing={2}>
          <Alert severity="info" sx={{ fontWeight: 600 }}>
            {data.banner || 'Teaching handover — prior history (read-only)'}
          </Alert>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Student & current assignment
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Chip label={`Student: ${data.student?.name || '—'}`} color="primary" variant="outlined" />
              {data.student?.displayId ? <Chip label={`ID: ${data.student.displayId}`} variant="outlined" /> : null}
              <Chip label={`New batch: ${data.currentAssignment?.batchName || '—'}`} variant="outlined" />
              <Chip label={`New teacher: ${data.currentAssignment?.teacherName || '—'}`} variant="outlined" />
              <Chip
                label={`Assigned: ${data.currentAssignment?.assignmentDate || '—'}`}
                color="secondary"
                variant="outlined"
              />
              <Chip label="Read-only" size="small" />
            </Stack>
          </Paper>

          {!data.available ? (
            <Alert severity="warning">
              No prior-teacher history for this assignment (no earlier batch with a different teacher, and no
              previous teacher on this batch). Monthly assessments and notes below are still shown when present.
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Notes
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Student notes
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {data.notes?.studentNotes || 'None'}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Assignment admin notes
            </Typography>
            {data.notes?.assignmentAdminNotes?.length ? (
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {data.notes.assignmentAdminNotes.map((n) => (
                  <Box key={`${n.batchId}-${n.assignmentDate}`}>
                    <Typography variant="caption" color="text.secondary">
                      {n.batchName} · {n.assignmentDate || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {n.notes}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2">None</Typography>
            )}
          </Paper>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Prior teaching history (last 10 classes each)
            </Typography>
            {!data.priorBatches?.length ? (
              <Alert severity="info">No prior class history to show.</Alert>
            ) : (
              data.priorBatches.map((batch) => (
                <Accordion
                  key={`${batch.batchId}:${batch.teacherId || 'none'}:${batch.source || 'prior_batch'}`}
                  defaultExpanded
                  disableGutters
                  sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack spacing={0.5} sx={{ width: '100%', pr: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {batch.batchName} — {batch.teacherName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {batch.source === 'same_batch_teacher_change'
                          ? `Previous teacher on this batch · ${batch.assignmentDate || '—'} → ${
                              batch.assignmentEndedDate || '—'
                            }`
                          : `Assignment ${batch.assignmentDate || '—'}${
                              batch.assignmentEndedDate ? ` → ${batch.assignmentEndedDate}` : ' (period end N/A)'
                            } · Status: ${batch.assignmentStatus}`}
                        {' · '}
                        Showing {batch.sessionsShown}/{batch.sessionsLimit} classes
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell>Attendance</TableCell>
                            <TableCell>Taught</TableCell>
                            <TableCell>Next plan</TableCell>
                            <TableCell>Homework</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {batch.sessions?.length ? (
                            batch.sessions.map((s) => (
                              <TableRow key={s.sessionId}>
                                <TableCell>
                                  {s.sessionDate}
                                  {s.sessionKind === 'makeup' ? ' (makeup)' : ''}
                                </TableCell>
                                <TableCell>
                                  {s.startTime || '—'}
                                  {s.endTime ? `–${s.endTime}` : ''}
                                </TableCell>
                                <TableCell>{s.attendanceStatus || '—'}</TableCell>
                                <TableCell sx={{ maxWidth: 280 }}>
                                  {s.hasClassLog ? s.classLogSummary || 'Logged' : 'No class log filed'}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 220 }}>{s.nextPlanSummary || '—'}</TableCell>
                                <TableCell sx={{ maxWidth: 180 }}>
                                  {s.homework?.message ||
                                    (s.homework?.pdfUrl ? 'PDF on file' : null) ||
                                    (s.homework?.sentAt ? `Sent ${String(s.homework.sentAt).slice(0, 10)}` : '—')}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Typography variant="body2" color="text.secondary">
                                  No eligible sessions found for this batch assignment window.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Monthly assessments (all filed)
            </Typography>
            {!data.monthlyAssessments?.length ? (
              <Alert severity="info">No monthly assessments filed for this student yet.</Alert>
            ) : (
              data.monthlyAssessments.map((report) => (
                <Accordion key={report.id} disableGutters sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontWeight: 700 }}>{report.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {report.status}
                        {report.batchName ? ` · Batch: ${report.batchName}` : ''}
                        {report.submittedAt
                          ? ` · Submitted: ${String(report.submittedAt).slice(0, 10)}`
                          : report.updatedAt
                            ? ` · Updated: ${String(report.updatedAt).slice(0, 10)}`
                            : ''}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="subtitle2" color="text.secondary">
                      Assessment summary
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {formatModulesFromAssessment(report.assessmentData) || 'No module details'}
                    </Typography>
                    {report.assessmentData?.overallRemarks ? (
                      <>
                        <Typography variant="subtitle2" color="text.secondary">
                          Overall remarks
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                          {report.assessmentData.overallRemarks}
                        </Typography>
                      </>
                    ) : null}
                    {report.remarks ? (
                      <>
                        <Typography variant="subtitle2" color="text.secondary">
                          Teacher remarks
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {report.remarks}
                        </Typography>
                      </>
                    ) : null}
                    {report.rejectionReason ? (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Rejection reason: {report.rejectionReason}
                      </Alert>
                    ) : null}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Read-only — filed by previous / other teacher. Not editable here.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}
